import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { DateTime } from 'luxon';
import { useNavigate, useParams } from 'react-router-dom';

import useActiveBanners from '../../../hooks/useActiveBanners/useActiveBanners';
import useHttpError from '../../../hooks/useHttpError/useHttpError';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import {
  BannerRequest,
  BannerResponse,
  BannerScope,
  defaultBannerTimezone,
  defaultTimezoneOption,
} from '../../../models/BannersModels';
import { reset, updateBannerForm } from '../../../stores/BannersStore';
import BannersService from '../../../services/Banners/BannersService';

function useBannersForm() {
  const [t] = useTranslation();
  const { id } = useParams();
  const editMode = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const snackBar = useSnackBar();
  const { getHttpReason } = useHttpError();
  const { fetchActiveBanners } = useActiveBanners();
  const [isLoading, setIsLoading] = useState(editMode);
  const [overlappingBanners, setOverlappingBanners] = useState<BannerResponse[]>([]);

  /**
   * Converts a UTC date from the API into a form date showing the same time as in the default timezone
   * @param date
   */
  const toDefaultTimezoneDate = (date?: string) =>
    date
      ? DateTime.fromISO(date, { zone: defaultBannerTimezone }).setZone('local', { keepLocalTime: true }).toISO() ?? ''
      : '';

  /**
   * Fetches the banner data used to prefill edit mode
   * @param bannerId
   */
  const fetchBanner = (bannerId: string) => {
    setIsLoading(true);
    BannersService.api.fetchBannerById(bannerId).then(
      (response) => {
        const banner = response.data;
        dispatch(
          updateBannerForm({
            bannerType: banner.bannerType,
            scope: banner.scopeType,
            selectedMarkets: banner.marketIds ?? [],
            selectedCompanies: banner.companyIds ?? [],
            bannerTitle: banner.title,
            timezone: defaultBannerTimezone,
            timezoneOption: defaultTimezoneOption,
            startDate: toDefaultTimezoneDate(banner.startAt),
            endDate: toDefaultTimezoneDate(banner.endAt),
            maintenanceTimezone: defaultBannerTimezone,
            maintenanceTimezoneOption: defaultTimezoneOption,
            maintenanceStart: toDefaultTimezoneDate(banner.maintenanceWindow?.startAt),
            maintenanceEnd: toDefaultTimezoneDate(banner.maintenanceWindow?.endAt),
            messages: banner.translations ?? {},
            dismissible: banner.dismissible,
          }),
        );
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.bannerOnboarding.fetchBannerError'), 'error');
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    if (!id) return;

    fetchBanner(id);
  }, [id]);

  /**
   * Creates a banner
   * @param params
   */
  const createBanner = (params: BannerRequest) => {
    setIsLoading(true);
    BannersService.api
      .createBanner(params)
      .then(
        () => {
          snackBar.showSnackBar(t('pages.bannerOnboarding.createBannerSuccess'), 'success');
          fetchActiveBanners();
          navigate('/banners');
          dispatch(reset());
        },
        (res) => {
          const error = getHttpReason(res.response.data, t('pages.bannerOnboarding.createBannerError'));
          snackBar.showSnackBar(error, 'error');
        },
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Updates a banner
   * @param params
   */
  const updateBanner = (params: BannerRequest) => {
    if (!id) return;

    setIsLoading(true);
    BannersService.api
      .updateBanner(id, params)
      .then(
        () => {
          snackBar.showSnackBar(t('pages.bannerOnboarding.updateBannerSuccess'), 'success');
          fetchActiveBanners();
          navigate('/banners');
          dispatch(reset());
        },
        (res) => {
          const error = getHttpReason(res.response.data, t('pages.bannerOnboarding.updateBannerError'));
          snackBar.showSnackBar(error, 'error');
        },
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Saves the banner, creating or updating it depending on the form mode
   * @param params
   */
  const saveBanner = (params: BannerRequest) => {
    if (editMode) {
      updateBanner(params);
    } else {
      createBanner(params);
    }
  };

  /**
   * Publishes the banner
   * On the first attempt it checks for scheduled or active banners overlapping the new one and, if there are any,
   * stops so they can be displayed to the user. Publishing again after that saves the banner anyway
   * @param params
   */
  const publishBanner = (params: BannerRequest) => {
    if (overlappingBanners.length > 0) {
      saveBanner(params);
      return;
    }

    setIsLoading(true);
    BannersService.api
      .checkBannerOverlaps({
        scopeType: params.scopeType as BannerScope,
        startAt: params.startAt || undefined,
        endAt: params.endAt || undefined,
        marketIds: params.scopeType === BannerScope.MARKETS ? params.marketIds : undefined,
        companyIds: params.scopeType === BannerScope.COMPANIES ? params.companyIds : undefined,
      })
      .then(
        (response) => {
          const overlapping = response.data.overlappedBanners.filter((banner) => banner.id !== id);
          if (overlapping.length > 0) {
            setOverlappingBanners(overlapping);
            setIsLoading(false);
          } else {
            saveBanner(params);
          }
        },
        () => {
          snackBar.showSnackBar(t('pages.bannerOnboarding.checkOverlappingBannersError'), 'error');
          setIsLoading(false);
        },
      );
  };

  /**
   * Clears the overlapping banners so they are checked again on the next publish
   */
  const resetOverlappingBanners = () => {
    setOverlappingBanners([]);
  };

  /**
   * Saves a banner as draft
   * @param params
   */
  const saveDraft = (params: BannerRequest) => {
    setIsLoading(true);
    BannersService.api
      .createBanner(params)
      .then(
        () => {
          snackBar.showSnackBar(t('pages.bannerOnboarding.saveDraftSuccess'), 'success');
          navigate('/banners');
          dispatch(reset());
        },
        (res) => {
          const error = getHttpReason(res.response.data, t('pages.bannerOnboarding.saveDraftError'));
          snackBar.showSnackBar(error, 'error');
        },
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  return { editMode, isLoading, overlappingBanners, publishBanner, saveDraft, resetOverlappingBanners };
}

export default useBannersForm;
